// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Local-development bridge flags (single source of truth).
 *
 * When the dev server is started with `VITE_AGENT_LOCAL=1`, the UI talks to a
 * locally-running agent (an AgentCore-compatible HTTP endpoint on :8080)
 * instead of a deployed Bedrock AgentCore runtime. In that mode the UI:
 *   - POSTs to a relative `/invocations` URL, which Vite proxies to the local
 *     agent (vite.config.ts server.proxy), so the browser sees same-origin;
 *   - skips the Cognito sign-in gate (the local shim does not validate JWTs);
 *   - does NOT require a real access token.
 *
 * This flag is read at build/serve time and tree-shaken out of production
 * builds (where VITE_AGENT_LOCAL is unset). It must never be set in a
 * deployed build.
 */
export const LOCAL_AGENT = import.meta.env.VITE_AGENT_LOCAL === "1"

/**
 * Agent pattern to use in local mode (selects the SSE parser). Defaults to a
 * generic single-agent Strands pattern. Override with VITE_AGENT_LOCAL_PATTERN
 * if iterating on a different pattern locally.
 */
export const LOCAL_AGENT_PATTERN =
  import.meta.env.VITE_AGENT_LOCAL_PATTERN || "strands-single-agent"

/**
 * A throwaway unsigned JWT for the Authorization header in local mode. The
 * local agent expects the header to be present but does not validate it. NEVER
 * accepted by a real deployed runtime.
 */
export const LOCAL_MOCK_JWT = (() => {
  const b64url = (o: unknown) =>
    btoa(JSON.stringify(o)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  return `${b64url({ alg: "none", typ: "JWT" })}.${b64url({ sub: "local-dev-user" })}.`
})()
